import { CloseOutlined } from '@ant-design/icons';
import { useMemo, useState } from 'react';

import { Flex, ScrollVFlex, Space, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import IconButton from '@/components/Button/IconButton';
import TransitionPortal from '@/components/Common/TransitionPortal';
import Heading from '@/components/Typography/Heading';
import { SectionTitle } from '@/components/Typography/NamedTypo';
import Typo from '@/components/Typography/Typo';
import DnDFileUploader from '@/features/CsvImport/DnDFileUploader';
import FieldTable, { type DocumentRow } from '@/features/CsvImport/FieldTable';
import UploadPreview, {
  type SimpleCol,
  type SimpleRow,
  UPLOAD_STATE,
} from '@/features/CsvImport/UploadPreview';
import useColor from '@/hooks/domain/useColor';
import useToastStore from '@/stores/pure/ToastStore';

const TEXT_FILTER_FIELD = 'text_filter';
const ESSENTIALFIELDS = [[TEXT_FILTER_FIELD]];

type FilterItemsUploadTextModalProps = {
  isOpen: boolean;
  onConfirm: (addedTexts: string[]) => void;
  closeModal: () => void;
  existingTexts: string[];
};

const MAX_UPLOAD_ROWS = 1000;
const MODAL_MARGIN = 32;

type FieldDocData = SimpleCol & DocumentRow;

const columns: FieldDocData[] = [
  {
    columnKey: TEXT_FILTER_FIELD,
    colWidth: 1400,
    type: 'string',
    required: true,
    desc: 'Text to exclude',
  },
];

const FilterItemsUploadTextModal = (props: FilterItemsUploadTextModalProps) => {
  const color = useColor();
  const { addDefaultToast } = useToastStore((state) => ({
    addDefaultToast: state.addDefaultToast,
  }));
  const [errorMsg, setErrorMsg] = useState('');
  const [mountComplete, setMountComplete] = useState(false);
  const [uploading, setUploading] = useState<UPLOAD_STATE>(UPLOAD_STATE.READY);
  const [progress, setProgress] = useState({
    current: 1,
    total: 100,
  });
  const [dragActive, setDragActive] = useState(false);
  const [formattedData, setFormattedData] = useState<string[]>([]);

  const previewData: SimpleRow[] = useMemo(() => {
    return formattedData.slice(0, 100).map((row, index) => {
      return {
        rowKey: index,
        rowData: [
          {
            columnKey: TEXT_FILTER_FIELD,
            rawData: row,
          },
        ],
      };
    });
  }, [formattedData]);

  const initialize = () => {
    setFormattedData([]);
    setErrorMsg('');
    setMountComplete(false);
    setUploading(UPLOAD_STATE.READY);
    setProgress({
      current: 1,
      total: 100,
    });
  };

  const confirmUpload = async () => {
    setUploading(UPLOAD_STATE.UPLOADING);
    setProgress((state) => ({
      ...state,
      total: formattedData.length,
    }));
    try {
      props.onConfirm(formattedData);
      setUploading(UPLOAD_STATE.COMPLETE);
      addDefaultToast('The file has been successfully uploaded', 'success');
      initialize();
      props.closeModal();
    } catch {
      setUploading(UPLOAD_STATE.READY);
      addDefaultToast('Upload Fail', 'fail');
    }
  };

  const uploadData = async (
    data: Record<typeof TEXT_FILTER_FIELD, string>[],
    fileFields: string[],
  ) => {
    try {
      ESSENTIALFIELDS.forEach((field) => {
        if (data.length > MAX_UPLOAD_ROWS) {
          throw new Error(
            'The number of rows in the file cannot exceed 1000. Please check the contents and try again!',
          );
        }

        if (
          Array.isArray(field) &&
          field.filter((v) => fileFields.includes(v)).length == 0
        ) {
          throw new Error(
            'Some of your data is formatted in a way we are not familiar with. Check the headers and try again!',
          );
        } else if (typeof field === 'string' && !fileFields?.includes(field)) {
          throw new Error(
            'Some of your data is formatted in a way we are not familiar with. Check the headers and try again!',
          );
        }
      });

      data.forEach((data) => {
        if (data[TEXT_FILTER_FIELD] === '') {
          throw new Error(
            'raw_text must be filled. Check the contents and try again!',
          );
        }
      });

      const map = new Map();

      props.existingTexts.forEach((text) => {
        map.set(text, true);
      });

      data.forEach((d) => {
        if (map.has(d[TEXT_FILTER_FIELD])) {
          throw new Error(
            'There are duplicate values in the raw_text field. Check the contents and try again!',
          );
        }
        map.set(d[TEXT_FILTER_FIELD], true);
      });

      setFormattedData(data.map((d) => d.text_filter));
      setErrorMsg('');
      onComplete();
    } catch (e: any) {
      setErrorMsg(e.message);
      onFail();
    }
  };

  const onComplete = () => {
    setMountComplete(true);
    addDefaultToast('Successfully Completed', 'success');
  };

  const onFail = () => {
    setMountComplete(false);
    addDefaultToast('Upload Fail', 'fail');
  };

  const handleCloseModal = () => {
    props.closeModal();
  };

  return (
    <TransitionPortal
      layerId="modal-layer"
      status={props.isOpen}
      close={props.closeModal}
      duration={300}
      style={{
        top: MODAL_MARGIN,
        left: MODAL_MARGIN,
        minWidth: '1200px',
        width: `calc(100vw - ${MODAL_MARGIN * 2}px)`,
        height: `calc(100vh - ${MODAL_MARGIN * 2}px)`,
        borderRadius: 20,
        boxShadow: 'none',
        padding: 40,
      }}
      background={{
        enable: true,
        style: {
          backdropFilter: 'blur(14px)',
          backgroundColor: color.FD40,
        },
      }}
    >
      <VFlex height="100%">
        <Flex j="space-between">
          <div>
            <Heading type="h2" className="text-gray-900">
              Upload a CSV file to the exclusion list
            </Heading>
            <Space h={8} />
            <Typo type="label3" className="text-gray-800">
              Upload a file of words and phrases to be excluded from AI
              analysis.
            </Typo>
          </div>

          <IconButton icon={<CloseOutlined />} onClick={props.closeModal} />
        </Flex>
        <Space h={32} />

        {mountComplete ? (
          <UploadPreview
            columns={columns}
            total={formattedData.length}
            datas={previewData}
            progress={progress}
            uploading={uploading}
            confirmUpload={confirmUpload}
            closeUploadModal={handleCloseModal}
          />
        ) : (
          <ScrollVFlex f={1} g={20}>
            {errorMsg && (
              <Typo weight="sb" color={color.red600} style={{ height: 24 }}>
                {errorMsg}
              </Typo>
            )}
            <VFlex
              g={16}
              padding="20px 24px"
              style={{ backgroundColor: color.gray200, borderRadius: 8 }}
            >
              <Typo type="body2" color={color.gray700}>
                To exclude specific words or phrases, upload a CSV or Excel
                file. Download the sample file for guidance.
              </Typo>
              <a
                href="/assets/sample_filter_items.csv"
                download="Syncly CSV import Sample.csv"
                target="_self"
                style={{ width: 'fit-content' }}
              >
                <Button
                  variant="outlined"
                  sizeType="m"
                  style={{ width: 'fit-content' }}
                >
                  Download sample CSV
                </Button>
              </a>
            </VFlex>
            <DnDFileUploader
              uploadData={uploadData}
              dragActive={dragActive}
              setDragActive={setDragActive}
            />
            <VFlex g={8}>
              <SectionTitle>Feedback field description</SectionTitle>
              <FieldTable rows={columns} />
            </VFlex>
          </ScrollVFlex>
        )}
      </VFlex>
    </TransitionPortal>
  );
};

export default FilterItemsUploadTextModal;
